import { Injectable } from '@angular/core';

import { of, Observable, tap, switchMap, merge, map, pairwise, filter, take, combineLatest } from 'rxjs';

import { Resource } from '@shared/resource/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import {
  AuthRestaurantService,
  ResourceQuery,
  ResourceStore,
  SocketService,
} from '@tablesready/frontend-store';
import { ResourceApiService } from './resource-api.service';

@Injectable({providedIn: 'root'})
export class ResourcesService {
  constructor(
    private readonly apiService: ResourceApiService,
    private readonly store: ResourceStore,
    private readonly query: ResourceQuery,
    private readonly socketService: SocketService,
    private readonly authRestaurantService: AuthRestaurantService,
  ) {
  }

  load() {
    return this.authRestaurantService.resourceSettings()
      .pipe(
        take(1),
        switchMap(settings => settings.enabled ? this.apiService.getAll() : of([])),
        tap(resources => this.store.set(resources)),
      );
  }

  getById(resourceId: string): Resource {
    return this.query.getEntity(resourceId);
  }

  getEnabledByIds(ids: string[] = []): Resource[] {
    return this.query.getAll({
      filterBy: [entity => ids.includes(entity._id) && entity.enabled],
    });
  }

  selectAll(): Observable<Resource[]> {
    return this.query.selectAll();
  }

  selectAllEnabled(): Observable<Resource[]> {
    return this.selectAll()
      .pipe(map(resources => resources.filter(resource => resource.enabled)));
  }

  isEnabled(): Observable<boolean> {
    return combineLatest([
      this.authRestaurantService.resourceSettings(),
      this.selectAllEnabled(),
    ])
    .pipe(
      map(([{ enabled }, services]) => (enabled || false) && services.length > 0)
    );
  }

  selectAllSorted(): Observable<Resource[]> {
    return combineLatest([
      this.authRestaurantService.resourceSettings(),
      this.selectAll(),
    ])
      .pipe(
        map(([settings, resources]) => {
          const sortColumn = settings.sort || 'name';

          return [...resources].sort((a, b) => {
            if (a[sortColumn] < b[sortColumn]) {
              return -1;
            }

            if (a[sortColumn] > b[sortColumn]) {
              return 1;
            }

            return a._id < b._id ? -1 : 1;
          });
        }),
      );
  }

  selectAllSortedByName(): Observable<Resource[]> {
    return this.selectAll().pipe(
      map(resources => {
        return resources.sort((resource1, resource2) => {
          if (resource1.name === resource2.name) {
            return 0;
          }

          return resource1.name > resource2.name ? 1 : -1;
        });
      })
    );
  }

  listenToChanges() {
    return merge(
      this.listenToEnablingDisabling(),
      this.socketService.getEventStream<Resource[]>(WebSocketEventsEnum.resourceCreated)
        .pipe(tap(resources => this.store.add(resources))),
      this.socketService.getEventStream<Resource>(WebSocketEventsEnum.resourceUpdated)
        .pipe(tap(resource => this.store.update(resource._id, resource))),
      this.socketService.getEventStream<void>(WebSocketEventsEnum.resourceUpdatedBulk)
        .pipe(switchMap(() => this.load())),
      this.socketService.getEventStream<string[]>(WebSocketEventsEnum.resourceDeleted)
        .pipe(tap(ids => this.store.remove(ids))),
    );
  }

  reset() {
    this.store.reset();
  }

  private listenToEnablingDisabling() {
    return this.authRestaurantService.resourceSettings()
      .pipe(
        pairwise(),
        filter(([prev, current]) => prev.enabled !== current.enabled),
        switchMap(() => this.load()),
      );
  }
}
